import { Helmet } from "react-helmet"
import {
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	Container,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	MenuItem,
	Paper,
	Select,
	Step,
	StepContent,
	Stepper,
	StepLabel,
	StepConnector,
	Tooltip,
	tooltipClasses,
	Typography,
} from "@mui/material"
import {
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { makeStyles, styled } from "@mui/styles"
import cssVariables from "../../../../theme/cssVariables"
import ApexCharts from "react-apexcharts"
import * as Util from "../../../../services/global.service"

import ConfirmInboxDialog from "../../../../components/inbox/ConfirmDialog";
import MessageDialog from "../../../../components/inbox/MessageDialog";
import DetailDialog from "../../../../components/calibration/DetailDialog"
import ReadMoreDialog from "../../../../components/inbox/ReadMoreDialog"
import { getCalibrationDetail, getEmployeeCalibrationDetail, postWorkflowSubmit, postWorkflowReject } from "../../../../actions/calibration"

import { ReactComponent as MarkIcon } from "../../../../assets/icons/mark.svg";
import { ReactComponent as CheckIcon } from "../../../../assets/icons/check-2.svg";
import { ReactComponent as CancelIcon } from "../../../../assets/icons/cancel.svg";
import { ReactComponent as PeopleGroupIcon } from "../../../../icons/people-group.svg"
import { ReactComponent as StarsGroupIcon } from "../../../../icons/stars-group.svg"
import { ReactComponent as StarsGroupInactiveIcon } from "../../../../icons/stars-group-inactive.svg"
import { ReactComponent as DartsIcon } from "../../../../icons/darts.svg"
import { ReactComponent as CalibrationAlertIcon } from "../../../../icons/danger.svg"
import { ReactComponent as DocumentIcon } from "../../../../icons/document.svg"
import { ReactComponent as StarIcon } from "../../../../icons/star.svg"
import { ReactComponent as MinimizeIcon } from "../../../../assets/icons/minimized.svg";
import { ReactComponent as MaximizeIcon } from "../../../../assets/icons/maximized.svg";
import personImg from "../../../../assets/icons/person-fill.svg";


const BootstrapTooltip = styled(({ className, ...props }) => (
	<Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.arrow}`]: {
		color: "#343A40",
	},
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "#343A40",
		color: "#FFFFFF",
		padding: "11px 15px",
	},
}))

const statusBox = (data, isCenter = false) => {
	let fontColor = "#404349"
	let backgroundColor = "#A0A0A0"
	let text = "-"
	if (data?.toLowerCase() === "in progress" || data?.toLowerCase() === "ระหว่างดำเนินการ") {
		fontColor = "#EFB76D"
		backgroundColor = "rgba(239, 183, 109, 0.1)"
		text = data
	} else if (data?.toLowerCase() === "pending" || data?.toLowerCase() === "รออนุมัติ") {
		fontColor = "#F28E2B"
		backgroundColor = "rgba(242, 142, 43, 0.1)"
		text = data
	} else if (data?.toLowerCase() === "completed" || data?.toLowerCase() === "approved" || data?.toLowerCase() === "เสร็จสิ้น" || data?.toLowerCase() === "อนุมัติ" || data?.toLowerCase() === "อนุมัติเสร็จสิ้น") {
		fontColor = "#5DA196"
		backgroundColor = "rgba(93, 161, 150, 0.1)"
		text = data
	} else if (data?.toLowerCase() === "rejected" || data?.toLowerCase() === "ปธิเสธ") {
		fontColor = "#CC6058"
		backgroundColor = "#CC605826"
		text = data
	}

	return (
		<Box sx={{ backgroundColor: backgroundColor ?? "#fff", width: '120px', paddingTop: '8px', paddingBottom: '8px', height: '40px', textAlign: 'center', borderRadius: '5px', margin: isCenter ? '0 auto' : 'none', width: "130px", height: "40px" }}>
			<Typography fontSize={16} lineHeight="21px" fontWeight="500" color={fontColor}>
				{Util.upperFirstWord(text)}
			</Typography>
		</Box>
	)
}

// main component
const InboxCalibrationDetail = (props) => {
	const { t, i18n } = useTranslation()
	const dispatch = useDispatch()
	const { serial, messageSerial } = useParams()

	const calibrationState = useSelector((state) => state.Calibration)

	// scroll table
	const [canEventFire, setCanEventFire] = React.useState(true)
	useEffect(() => {
		const slider = document.querySelector('#calibration-table')
		let isDown = false
		let startX
		let scrollLeft
		let isClicked = true
		const mouseDown = (e) => {
			isClicked = true
			isDown = true
			startX = e.pageX - slider.offsetLeft
			scrollLeft = slider.scrollLeft
		}

		const mouseLeave = () => {
			isDown = false
		}

		const mouseUp = () => {
			if (!isClicked) {
				setCanEventFire(false)
			} else {
				setCanEventFire(true)
			}
			isClicked = true
			isDown = false
		}

		const mouseMove = (e) => {
			isClicked = false
			if (!isDown) return
			const x = e.pageX - slider.offsetLeft
			const scrollSpeed = (x - startX) * 1 //scroll-speed
			slider.scrollLeft = scrollLeft - scrollSpeed
		}
		slider.addEventListener('mousedown', mouseDown)
		slider.addEventListener('mouseleave', mouseLeave)
		slider.addEventListener('mouseup', mouseUp)
		slider.addEventListener('mousemove', mouseMove)

		return () => {
			slider.removeEventListener('mousedown', mouseDown)
			slider.removeEventListener('mouseleave', mouseLeave)
			slider.removeEventListener('mouseup', mouseUp)
			slider.removeEventListener('mousemove', mouseMove)
		}
	}, [])
	// end scroll table

	const styles = {
		link: {
			color: "#5DA196",
			cursor: "pointer",
		},
		end: {
			color: "#A3A3A3",
		},
	}
	const useStyles = makeStyles({
		iconContainer: {
			width: 40,
			height: 40,
			borderRadius: 5,
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
		},
		paperDetailContainer: {
			backgroundColor: `${cssVariables.color.gray}!important`,
			width: "100%",
			padding: "1.5rem",
		},
		blueBg: {
			background: "#6694CC",
		},
		profileContainer: {
			width: 60,
			height: 60,
			borderRadius: "50%",
			marginTop: 3,
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
		},
		statusBadge: {
			width: "150px",
			borderRadius: "5px",
			padding: "8px 10px",
			textAlign: "center",
		},
		draftBg: {
			background: "#EFB76D26",
		},
		draftText: {
			color: "#EFB76D",
		},
		pendingBg: {
			background: "#F280261A",
		},
		pendingText: {
			color: "#EFB76D",
		},
		approveBg: {
			background: "#5DA19626",
		},
		approveText: {
			color: "#5DA196",
		},
		rejectedBg: {
			background: "#CC605826",
		},
		rejectedText: {
			color: "#CC6058",
		},
		grayBg: {
			background: "#82828226",
		},
		grayText: {
			color: "#828282!important",
		},
		cancelBg: {
			background: "#82828226",
		},
		cancelText: {
			color: "#828282",
		},
		cancelPendingBg: {
			background: "#82828226",
		},
		cancelPendingText: {
			color: "#828282",
		},
		grayStepBg: {
			background: "#828282",
		},
		stepTextRoot: {
			fill: "#939393!important",
		},
		processBg: {
			background: "rgba(239, 183, 109, 0.15)",
		},
		completedBg: {
			background: "rgba(93, 161, 150, 0.15)",
		},
		redBg: {
			background: "rgba(204, 96, 88, 0.15)",
		},
		processText: {
			color: "#EFAD0A!important",
		},
		completedText: {
			color: "#5DA196!important",
		},
		redText: {
			color: "#CC6058!important",
		},
		primaryColorSvg: {
			"& > path": {
				stroke: cssVariables.color.primary,
			}
		},
		starIconSvg: {
			width: "20px",
			height: "20px"
		},
		clickable: {
			cursor: "pointer",
			transition:
				"color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
			"&:hover": {
				color: "rgb(65, 112, 105) !important",
			},
		},
		boxStyled: {
			boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)!important",
			borderRadius: "10px!important"
		},
		calibrationCardRoot: {
			boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)!important",
			borderRadius: "5px!important",
			width: "268px",
			height: "812px",
			overflow: "auto",
			padding: "15px",
			flexShrink: 0
		},
		calibrationAlert: {
			backgroundColor: "#CC6058!important",
			borderRadius: "4px",
			height: "40px",
			padding: "10px 7px",
			boxShadow: "none!important",
			display: "flex",
			alignItems: "center"
		},
		calibrationItemCard: {
			borderRadius: "5px",
			height: "136px",
			padding: "11px 12px",
			boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.1)!important",
			display: "flex",
			flexDirection: "column"
		},
		calibrationItemGrid: {
			borderRadius: "5px",
			cursor: "pointer",
			"&:hover": {
				outline: `1px solid ${cssVariables.color.primary}`
			}
		},
		btnDisabled: {
			backgroundColor: "#AAAAAA!important",
			color: "#FFF!important",
			border: "none!important"
		},
		paddingRight_8: {
			paddingRight: "8px",
		},
		paddingRight_0: {
			paddingRight: "0!important",
		},
	})
	const classes = useStyles()

	const CustomStepContent = (props) => {
		const { t } = useTranslation();
		const language = localStorage.getItem("lang")
		let step = props.data
		let divider = props.divider ?? null
		return (
			<StepContent>
				<Box sx={{ display: "flex" }}>
					{step.employeeStatus === 3 ? (
						<div
							className={`${classes.profileContainer}`}
							style={{
								width: 40,
								height: 40,
								background: "linear-gradient(0deg, #CCCCCC, #CCCCCC)",
							}}
						>
							<img
								style={{ width: 30, height: 30 }}
								src={personImg}
								alt="profile"
								onError={({ currentTarget }) => {
									currentTarget.onerror = null;
									currentTarget.src = personImg;
								}}
							/>
						</div>
					) : step.profileImage !== "" || step.profileImage ? (
						<img
							className={`${classes.profileContainer}`}
							style={{
								width: 40,
								height: 40,
								opacity: step.employeeStatus === 2 ? "0.3" : "1",
							}}
							src={step.employeeStatus === 3 ? personImg : step.profileImage}
							alt="profile"
							onError={({ currentTarget }) => {
								currentTarget.onerror = null;
								currentTarget.src = personImg;
							}}
						/>
					) : (
						<Avatar
							className={`${classes.profileContainer}`}
							style={{
								width: 40,
								height: 40,
								opacity: step.employeeStatus === 2 ? "0.3" : "1",
							}}
						/>
					)}
					<Box
						sx={{
							display: "flex",
							flexFlow: "column",
							marginLeft: "10px",
						}}
					>
						{step.employeeStatus === 3 ? (
							<React.Fragment>
								<Typography variant="s14_fw300">{t("label.noData")}</Typography>
								<Typography variant="s14_fw300">{t("label.date")}: -</Typography>
							</React.Fragment>
						) : (
							<React.Fragment>
								<Typography
									variant="s14_fw600"
									sx={{ opacity: step.employeeStatus === 2 ? "0.5" : "1" }}
								>
									{language === "en-US" ? step.fullname_en : step.fullname_th}
								</Typography>
								<Typography
									variant="s14_fw300"
									sx={{ opacity: step.employeeStatus === 2 ? "0.5" : "1" }}
								>
									{t("label.date")}: {Util.dateDayTimeFormat(step.date.replace(/[A-Za-z]/g, " ")) ?? "-"}
								</Typography>
							</React.Fragment>
						)}
						{step.employeeStatus === 3 ? (
							<React.Fragment>
								<Typography variant="s14_fw300">{t("label.noData")}</Typography>
							</React.Fragment>
						) : (
							<React.Fragment>
								{step.proxy !== undefined && step.proxy !== "" ?
									< Box sx={{ background: "#FAFAFA", "border-radius": "4px", padding: "3px 8px", margin: "10px 0px" }} >
										<Typography
											fontSize={12}
											lineHeight="18px"
											color="#9E9E9E"
										>
											{"Proxy by:"}
										</Typography>
										<Typography
											fontSize={12}
											lineHeight="18px"
											color="#5DA196"
										>
											{Util.formatData(step.proxy, "-")}
										</Typography>
									</Box>
									: null
								}
							</React.Fragment>
						)}
						{step.status ? (
							// <div
							// 	className={`${classes.statusBadge} ${classes[Util.StepStatusColor(step) + "Bg"]
							// 		}`}
							// >
							// 	<Typography
							// 		className={`${classes[Util.StepStatusColor(step) + "Text"]}`}
							// 		fontWeight={500}
							// 	>
							// 		{step.status_name}
							// 	</Typography>
							// </div>
							statusBox(step.status_name)
						) : (
							<></>
						)}
						{step.comment ? (
							<React.Fragment>
								<Typography variant="h6" style={{ marginTop: "10px" }}>
									{t("inbox.label.comment")}:
								</Typography>
								<Typography
									variant="s14_fw400"
									style={{
										marginTop: "0",
										wordBreak: "break-word",
										overflow: "hidden",
										textOverflow: "ellipsis",
										display: "-webkit-box",
										WebkitLineClamp: "2",
										WebkitBoxOrient: "vertical",
										color: "#404349",
									}}
									id="readmore"
								>
									{step.comment}
								</Typography>
								<Typography
									variant="s14_fw400"
									textAlign="center"
									onClick={() => props.readmore(step ?? {})}
									className="clickable"
								>
									<u>{t("inbox.label.readMore")}</u>
								</Typography>
							</React.Fragment>
						) : (
							<></>
						)}
					</Box>
				</Box>
				{divider ? <Divider sx={{ margin: "10px 0" }} /> : <></>}
			</StepContent >
		)
	}

	// handleOpenReadMoreDialog
	const [readMoreComment, setReadMoreComment] = React.useState("")
	const [openReadMoreDialog, setReadMoreDialog] = React.useState(false)
	const handleOpenReadMoreDialog = (value) => {
		setReadMoreComment(value)
		setReadMoreDialog(true)
	}
	const handleCloseReadMoreDialog = (value) => {
		setReadMoreDialog(false)
	}

	const navigate = useNavigate()
	const goTo = (data) => {
		navigate(data, { replace: false })
	}

	const [isDetailDialogOpen, setIsDetailDialogOpen] = React.useState(false)
	const [selectedDetailEmployeeData, setSelectedDetailEmployeeData] = React.useState({})
	const handleDetailDialog = (data, value) => {
		if (canEventFire) {
			dispatch(getEmployeeCalibrationDetail(serial, data?.serial ?? "")).then(resp => {
				setSelectedDetailEmployeeData(resp)
				setIsDetailDialogOpen(value)
			})
		}
	}

	const [cycleData, setCycleData] = React.useState({})
	const [summaryData, setSummaryData] = React.useState({
		total: 0,
		withRating: 0,
		noRating: 0
	})

	// chart data
	let chartOptions = {
		series: [{
			name: t("calibration.label.actual"),
			type: 'column',
			data: [0]
		}, {
			name: t("calibration.label.guideline"),
			type: 'line',
			data: [0]
		}],
		options: {
			noData: {
				text: "No Data",
				align: 'center',
				verticalAlign: 'middle',
			},
			chart: {
				type: "line",
				animations: {
					enabled: false
				},
				toolbar: {
					show: false,
				},
				selection: {
					enabled: false
				},
				zoom: {
					enabled: false
				}
			},
			stroke: {
				width: 2
			},
			dataLabels: {
				enabled: false,
				enabledOnSeries: false
			},
			colors: ["#5DA196", "#F19562"],
			markers: {
				size: 10,
				strokeWidth: 0,
			},
			plotOptions: {
				bar: {
					horizontal: false,
					borderRadius: 5,
					columnWidth: "25%",
				},
			},
			grid: {
				show: true,
			},
			title: {
				show: false,
			},
			xaxis: {
				axisBorder: {
					show: true,
					color: '#AAAAAA',
					height: 2,
					width: '100%',
					offsetX: 0,
					offsetY: 0
				},
				categories: [],
				labels: {
					formatter: function (val) {
						return val
					},
				},
				axisTicks: {
					show: false,
				},
				tooltip: {
					enabled: false
				}
			},
			yaxis: [{
				title: {
					text: undefined,
				},
				axisBorder: {
					show: true,
					color: '#AAAAAA',
					width: 2,
					offsetX: 3,
					offsetY: 0
				},
			}],
			tooltip: {
				enabled: true,
				enabledOnSeries: undefined,
				shared: true,
				followCursor: false,
				intersect: false,
				inverseOrder: false,
				custom: undefined,
				fillSeriesColor: false,
				style: {
					fontSize: "16px",
					fontFamily: "Kanit",
					fontWeight: 300,
				},
				marker: {
					show: false
				},
				onDatasetHover: {
					highlightDataSeries: false,
				},
				x: {
					show: false
				},
				y: {
					formatter: undefined,
					title: {
						formatter: (seriesName) => seriesName + ":",
					},
				},
				z: {
					formatter: undefined,
					title: "Size: ",
				},
				items: {
					display: "flex",
				},
				fixed: {
					enabled: false,
					position: "topRight",
					offsetX: 0,
					offsetY: 0,
				},
			},
			fill: {
				opacity: 1,
			},
			legend: {
				show: false,
			},
		},
	}

	const StyledPaper = (props) => {
		return (
			<Paper sx={{ padding: "20px" }} {...props}>
				{props.children}
			</Paper>
		)
	}

	const [chart, setChart] = React.useState(chartOptions)
	const [chartRawData, setChartRawData] = React.useState("")
	const [calibrationSessionData, setCalibrationSessionData] = React.useState("")
	const [ratingDecimal, setRatingDecimal] = React.useState(2)
	const [chartActual, setChartActual] = React.useState({ number: [], percent: [] })
	const [chartGuideline, setChartGuideline] = React.useState({ number: [], percent: [] })
	const [chartCategories, setChartCategories] = React.useState([])
	const [selectedChartType, setSelectedChartType] = React.useState("")
	const [action, setAction] = React.useState("")
	const [distributionData, setDistributionData] = React.useState([])
	const [toggleSteppers, setToggleSteppers] = React.useState(true);
	const [workflow, setWorkflow] = React.useState("")

	const formatWorkflowData = (data) => {
		let tmpArray = []
		const workflow = data?.workflow ?? false
		if (workflow) {
			let requester = workflow.requester ?? false;
			let approver = workflow.approver ?? false;
			if (requester) {
				let obj = {
					seq: 0,
					mark: "requester",
					proxy: requester.proxy ?? "-",
					position: t("inbox.label.requester"),
					fullname: requester.employee.name ?? "-",
					fullname_en: requester.employee.name_en ?? "-",
					fullname_th: requester.employee.name_th ?? "-",
					employee_position: requester.employee.position ?? "-",
					date: requester?.employee?.date?.Time ?? "",
					status: requester.status ?? "",
					status_name: requester.status_name,
					type: requester.workflow_type,
					profileImage: requester?.employee?.photo ?? "",
					active: true,
					employeeStatus: requester?.employee?.status
						? requester.employee.status == "active"
							? 1
							: 2
						: 2,
					comment: requester.employee.remark ?? "",
					stepStatus: requester.status_name
				}
				tmpArray.push(obj)
			}

			if (approver) {
				for (const [key, value] of Object.entries(approver)) {
					let isNeed = false;
					let obj = {
						seq: value[0].employee.seq ?? key,
						mark: "approver",
						proxy: value[0].proxy ?? "-",
						position: value[0].employee.position ?? "-",
						employee: [],
						// active: value[0].complete || value[0].is_current ? true : false,
						active: true,
						type: value[0].workflow_type,
					};
					value.forEach((item) => {
						obj.employee.push({
							fullname: item.employee.name ?? "-",
							fullname_en: item.employee.name_en ?? "-",
							fullname_th: item.employee.name_th ?? "-",
							employee_position: item.employee.position ?? "-",
							date: item?.employee?.date ?? "",
							status: item.is_current
								? item.status
								: item.status !== "wait"
									? item.status
									: "",
							status_name: item.status_name,
							profileImage: item?.employee?.photo ?? "",
							employeeStatus: item?.employee?.status
								? item.employee.status == "active"
									? 1
									: 2
								: 2,
							comment: item.employee.remark ?? "",
							proxy: item.proxy ?? "-",
						});
						isNeed = item.need_all
						obj.stepStatus = item.status_name
					})
					tmpArray.push(obj)
				}
			}
		}
		setWorkflow(tmpArray)
	}

	const handleSteppersTransition = () => {
		setToggleSteppers(!toggleSteppers);
	}

	const formatChartData = (data) => {
		let chartSeries = [{
			name: t("calibration.label.actual"),
			type: 'column',
			data: [0]
		}, {
			name: t("calibration.label.guideline"),
			type: 'line',
			data: [0]
		}]
		let totalActual = 0
		let tmpActualNumber = []
		let tmpActualPercent = []
		let tmpGuidelineNumber = []
		let tmpGuidelinePercent = []
		let tmpChartCategories = []
		data.forEach((item, index) => {
			tmpActualNumber.push(item?.actual ?? 0)
			tmpGuidelineNumber.push(item?.guideline?.number ?? 0)
			tmpGuidelinePercent.push(item?.guideline?.percent ?? 0)
			tmpChartCategories.push(`${index + 1} - ${item?.distribution ?? ""}`)
		})
		// cal actual percentage
		totalActual = tmpActualNumber.reduce((a, b) => a + b, 0)
		tmpActualNumber.forEach(actual => {
			tmpActualPercent.push(Math.round((actual / totalActual) * 100))
		})
		let totalActualPercent = 0
		totalActualPercent = tmpActualPercent.reduce((a, b) => a + b, 0)
		if (totalActualPercent > 100) {
			let exceedValue = totalActualPercent - 100
			for (let i = 0; exceedValue > 0; i++) {
				if (tmpActualPercent[i] > 0) {
					tmpActualPercent[i] = tmpActualPercent[i] - 1 // remove exceed 100 percent
					exceedValue--
				}
			}
		}

		setChartActual({ percent: tmpActualPercent, number: tmpActualNumber })
		setChartGuideline({
			number: tmpGuidelineNumber,
			percent: tmpGuidelinePercent
		})

		// do set chart data
		let tmpChartData = {
			...chart,
			...{
				series: chartSeries
			}
		}
		setChartCategories(tmpChartCategories)
		tmpChartData.options = {
			...tmpChartData.options,
			...{
				xaxis: {
					...tmpChartData.options.xaxis,
					categories: tmpChartCategories,
				},
			},
		}
		updatedChart(tmpChartData)
	}

	const handleDistributionData = (data) => {
		let distFormat = []
		Object.entries(data).forEach(([key, item]) => {
			let tmp = {
				serial: "",
				seq: key,
				name: "",
				guideline: "",
				employee: [{
					serial: "",
					name: "",
					score: "",
					rating: "",
					calibration: ""
				}]
			}
			tmp.serial = item?.distribution?.id ?? ""
			tmp.name = item?.distribution?.description ?? ""
			tmp.guideline = item?.guideline ?? 0
			tmp.employee = item?.calibration_employee ? item?.calibration_employee?.map(emp => {
				let splitName = emp?.employee?.fullname.split(" ")
				return {
					serial: emp?.employee?.serial,
					fName: splitName[0],
					lName: splitName[1],
					score: emp?.score,
					rating: emp?.rating_classify,
					calibration: emp?.calibration_classify
				}
			}) : []
			distFormat.push(tmp)
		})
		// do move `no score` to last position
		distFormat.push(distFormat.shift())

		setDistributionData(distFormat)
	}

	const updatedChart = (tmpChartData = null) => {
		if (!tmpChartData) tmpChartData = { ...chart }
		if (selectedChartType === "number") {
			tmpChartData = {
				...tmpChartData,
				...{
					series: [{
						name: t("calibration.label.actual"),
						type: 'column',
						data: chartActual?.number ?? [0]
					}, {
						name: t("calibration.label.guideline"),
						type: 'line',
						data: chartGuideline?.number ?? [0]
					}]
				}
			}
			tmpChartData.options = {
				...tmpChartData.options,
				...{
					xaxis: {
						...tmpChartData.options.xaxis,
						categories: chartCategories,
					},
					yaxis: [{
						...tmpChartData.options.yaxis,
						axisBorder: {
							show: true,
							color: '#AAAAAA',
							width: 2,
							offsetX: 3,
							offsetY: 0
						},
						labels: {
							...tmpChartData.options.yaxis.labels,
							formatter: (value) => { return `${value}` }
						}
					}],
				},
			}
			setChart(tmpChartData)
		} else if (selectedChartType === "percentage") {
			tmpChartData = {
				...tmpChartData,
				...{
					series: [{
						name: 'Actual',
						type: 'column',
						data: chartActual?.percent ?? [0]
					}, {
						name: 'Guideline',
						type: 'line',
						data: chartGuideline?.percent ?? [0]
					}]
				}
			}
			tmpChartData.options = {
				...tmpChartData.options,
				...{
					xaxis: {
						...tmpChartData.options.xaxis,
						categories: chartCategories,
					},
					yaxis: [{
						...tmpChartData.options.yaxis,
						axisBorder: {
							show: true,
							color: '#AAAAAA',
							width: 2,
							offsetX: 3,
							offsetY: 0
						},
						labels: {
							...tmpChartData.options.yaxis.labels,
							formatter: (value) => { return `${value}%` }
						}
					}],
				},
			}
			setChart(tmpChartData)
		}
	}

	const changeCalibrationTableEmployee = () => {
		let changedData = { ...calibrationState.changedCalibrationData }
		let originalData = JSON.parse(JSON.stringify(distributionData)) //deep copy
		let resultData = originalData

		let findEmp = null
		let findNewDist = null
		let newDistIndex = null
		Object.entries(changedData).forEach(([key, value]) => {
			resultData = originalData.map(dist => {
				findNewDist = null
				newDistIndex = null
				// find emp
				findEmp = dist.employee.find(emp => emp.serial === key)
				if (findEmp) {
					// find destination dist
					if (dist.serial !== value) {
						findNewDist = originalData.find(newDist => newDist.serial === value)
						newDistIndex = originalData.findIndex(newDist => newDist.serial === value)
						// case changed : find & remove on old dist
						dist.employee = dist.employee.filter(remove => remove.serial !== key)
						// prepare employee to new dist data
						if (findNewDist && newDistIndex > -1) {
							findNewDist.employee = [
								...findNewDist.employee,
								...[
									{
										...findEmp,
										calibration: {
											id: findNewDist.serial,
											seq: parseInt(findNewDist.seq)
										}
									}
								]
							]
							// set employee to new dist data
							resultData[newDistIndex] = findNewDist
						}
					}
				}
				return dist
			})
		})
		setDistributionData(resultData)
	}

	const fetchAndSetData = () => {
		dispatch(getCalibrationDetail(`${serial}?view=inbox&message_id=${messageSerial}`)).then(response => {
			const resp = response.data

			let summary = resp?.calibration_summary
			let cycle = resp?.cycle
			let session = resp?.calibration_session
			let chart = resp?.calibration_graph
			let distribution = resp?.calibration_distribution
			let action = resp?.action
			setAction(action)
			handleDistributionData(distribution)
			setSummaryData({ total: summary?.total ?? 0, withRating: summary?.with_rating ?? 0, noRating: summary?.no_rating })
			setCycleData(cycle)
			setChartRawData(chart)
			setCalibrationSessionData(session)
			dispatch({
				type: "SET_RATE_DECIMAL",
				payload: session?.rate_decimal ?? 2
			})
			setRatingDecimal(session?.rate_decimal ?? 2)
			setSelectedChartType("number")
			formatWorkflowData(resp)
		})
	}

	const [messageSuccess, setMessageSuccess] = React.useState("")
	const [failMessage, setMessageFail] = React.useState("")
	// message dialog
	const [openSucceedMessageDialog, setSucceedMessageDialog] =
		React.useState(false)
	const handleOpenSucceedMessageDialog = (value) => {
		setSucceedMessageDialog(true)
	}
	const handleCloseSucceedMessageDialog = (value) => {
		setSucceedMessageDialog(false)
		navigate(`/kpi/inbox`, { replace: false })
	}
	const [openFailedMessageDialog, setFailedMessageDialog] =
		React.useState(false)
	const handleOpenFailedMessageDialog = () => {
		setFailedMessageDialog(true)
	}
	const handleCloseFailedMessageDialog = () => {
		setFailedMessageDialog(false);
		navigate(`/kpi/inbox`, { replace: false })
	}

	const [openApproveConfirmDialog, setApproveConfirmDialog] =
		React.useState(false)
	const handleOpenApproveConfirmDialog = () => {
		setApproveConfirmDialog(true)
	}
	const handleCloseApproveConfirmDialog = () => {
		setApproveConfirmDialog(false)
	}
	const handleApproval = (value) => {
		let approveData = {
			calibration_session_id: serial ?? "",
			remark: value.description,
		}
		dispatch(postWorkflowSubmit(approveData))
			.then(() => {
				fetchAndSetData()
				setApproveConfirmDialog(false);
				setMessageSuccess(
					t("calibration.label.dialog.approvalSuccess")
				)
				handleOpenSucceedMessageDialog(true);
			})
			.catch(() => {
				setApproveConfirmDialog(false);
				setMessageFail(
					t("calibration.label.dialog.approvalFail")
				)
				handleOpenFailedMessageDialog(true);
			})
	}
	const [openRejectConfirmDialog, setRejectConfirmDialog] =
		React.useState(false);
	const handleOpenRejectConfirmDialog = (value) => {
		setRejectConfirmDialog(true)
	}
	const handleCloseRejectConfirmDialog = (value) => {
		setRejectConfirmDialog(false)
	}
	const handleReject = (value) => {
		let rejectData = {
			calibration_session_id: serial ?? "",
			remark: value.description,
		};
		dispatch(postWorkflowReject(rejectData))
			.then(() => {
				fetchAndSetData()
				setRejectConfirmDialog(false);
				setMessageSuccess(
					t("calibration.label.dialog.rejectSuccess")
				)
				handleOpenSucceedMessageDialog(true);
			})
			.catch(() => {
				setRejectConfirmDialog(false);
				setMessageFail(
					t("calibration.label.dialog.rejectFail")
				)
				handleOpenFailedMessageDialog(true);
			})
	}

	// --------------------------------------------------------------------------------- useEffect here ---------------------------------------------------------------------------------
	useEffect(() => {
		fetchAndSetData()
		return () => {
			dispatch({
				type: "CLEAR_CALIBRATION_DATA"
			})
		}
	}, [])

	useEffect(() => {
		changeCalibrationTableEmployee()
	}, [calibrationState])

	useEffect(() => {
		updatedChart()
	}, [selectedChartType, chartActual, chartGuideline, chartCategories])

	// set chart data
	useEffect(() => {
		let tmpChartData = [...chartRawData]
		let tmpDistData = [...distributionData]
		tmpDistData.forEach(dist => {
			let findChartIndex = tmpChartData.findIndex(chartItem => chartItem.distribution === dist.name)
			if (findChartIndex > -1) {
				if (tmpChartData?.[findChartIndex]) {
					tmpChartData[findChartIndex].actual = dist?.employee?.length ?? 0
				}
			}
		})
		formatChartData(tmpChartData)
	}, [distributionData, chartRawData])

	// --------------------------------------------------------------------------------- end useEffect ---------------------------------------------------------------------------------

	return (
		<>
			<Helmet>
				<title>Inbox - Calibration | PMS </title>
			</Helmet>
			<Box
				sx={{
					backgroundColor: "background.default",
					minHeight: "100%",
					py: 3,
				}}
			>
				<Container maxWidth="xl">
					<Grid container py={2}>
						<Typography fontSize={16} lineHeight={1} color="#68717A">
							<span
								style={styles.link}
								onClick={() => goTo("/kpi/home")}
							>
								{t("label.home")}
							</span>{" "}
							/
							<span
								style={styles.link}
								onClick={() => goTo("/kpi/inbox")}
							>
								{" "} {t("label.inbox")}
							</span>{" "}
							/ <span style={styles.end}> {calibrationSessionData?.name ?? "-"}  </span>
						</Typography>
					</Grid>
					<Grid container mt={2} mb={3}>
						<Grid item xs={6} display="flex">
							<Typography
								variant="h1"
								fontSize={36}
								lineHeight="39.6px"
								fontWeight="500"
								color="#404349"
								sx={{ mr: "45px" }}
							>
								{calibrationSessionData?.name ?? "-"}
							</Typography>
							{statusBox(calibrationSessionData?.status_name)}
						</Grid>
						<Grid item xs={3} ml="auto">
							<Box sx={{ height: "45px", background: "#FFF", border: "1px solid #E4E4E4", borderRadius: "4px", p: "10px", display: "flex", alignItems: "center" }}>
								<Typography fontSize="16px" fontWeight="500" color="#404349">{cycleData?.name ?? "-"}</Typography>
							</Box>
						</Grid>
						<Grid item xs={3} ml="auto" >
							<Box display="flex" sx={{ width: "fit-content", ml: "auto" }}>
								{
									action?.reject &&
									(
										<Button
											color="red"
											variant="contained"
											startIcon={<MarkIcon />}
											sx={{ marginRight: 1, textTransform: "none", width: "139px", height: "45px" }}
											onClick={handleOpenRejectConfirmDialog}
										>
											<Typography fontSize="16px" fontWeight="600">{t("inbox.label.reject")}</Typography>
										</Button>
									)
								}
								{
									action?.approve &&
									(
										<Button
											color="primary"
											variant="contained"
											startIcon={<CheckIcon />}
											sx={{ textTransform: "none", width: "139px", height: "45px" }}
											onClick={handleOpenApproveConfirmDialog}
										>
											<Typography fontSize="16px" fontWeight="600">{t("inbox.label.approve")}</Typography>
										</Button>
									)
								}
							</Box>
						</Grid>
					</Grid>
					<Grid container spacing={2} mt={0} mb={0} wrap="nowrap" item xs={12}>
						<Grid item xs={toggleSteppers ? 9 : true} sx={!toggleSteppers && { maxWidth: "calc(100% - 87px)" }}>
							<Grid container spacing={4}>
								<Grid item xs={4}>
									<Paper sx={{ height: "140px", display: "flex", alignItems: "center", padding: "20px" }} classes={{ root: classes.boxStyled }}>
										<Grid container spacing={2}>
											<Grid
												item
												display="flex"
												alignItems="center"
												xs={4}
											>
												<PeopleGroupIcon className={classes.icon} />
											</Grid>
											<Grid
												item
												xs={8}
												display="flex"
												flexDirection="column"
												alignItems="center"
											>
												<Grid container item mb={0.5}>
													<Typography fontWeight="600" fontSize="48px" lineHeight={1}>
														{summaryData?.total ?? 0}
													</Typography>
												</Grid>
												<Grid container item mt={0.5}>
													<Typography fontWeight="400" fontSize="14px" color="#AAAAAA">
														{t("calibration.label.totalEmployee")}
													</Typography>
												</Grid>
											</Grid>
										</Grid>
									</Paper>
								</Grid>
								<Grid item xs={4}>
									<Paper sx={{ height: "140px", display: "flex", alignItems: "center", padding: "20px", maxWidth: "100%" }} classes={{ root: classes.boxStyled }}>
										<Grid container spacing={2}>
											<Grid
												item
												display="flex"
												alignItems="center"
												xs={4}
											>
												<StarsGroupIcon className={classes.icon} />
											</Grid>
											<Grid
												item
												xs={8}
												display="flex"
												flexDirection="column"
												alignItems="center"
											>
												<Grid container item mb={0.5}>
													<Typography fontWeight="600" fontSize="48px" lineHeight={1}>
														{summaryData?.withRating ?? 0}
													</Typography>
												</Grid>
												<Grid container item mt={0.5}>
													<Typography fontWeight="400" fontSize="14px" color="#AAAAAA">
														{t("calibration.label.totalEmployeeWithScore")}
													</Typography>
												</Grid>
											</Grid>
										</Grid>
									</Paper>
								</Grid>
								<Grid item xs={4}>
									<Paper sx={{ height: "140px", display: "flex", alignItems: "center", padding: "20px" }} classes={{ root: classes.boxStyled }}>
										<Grid container spacing={2}>
											<Grid
												item
												display="flex"
												alignItems="center"
												xs={4}
											>
												<StarsGroupInactiveIcon className={classes.icon} />
											</Grid>
											<Grid
												item
												display="flex"
												flexDirection="column"
												alignItems="center"
												xs={8}
											>
												<Grid container item mb={0.5}>
													<Typography fontWeight="600" fontSize="48px" lineHeight={1}>
														{summaryData?.noRating ?? 0}
													</Typography>
												</Grid>
												<Grid container item mt={0.5}>
													<Typography fontWeight="400" fontSize="14px" color="#AAAAAA">
														{t("calibration.label.noScore")}
													</Typography>
												</Grid>
											</Grid>
										</Grid>
									</Paper>
								</Grid>
							</Grid>
							<Grid container spacing={4} mt={1}>
								<Grid item lg={12} md={12}>
									<Card classes={{ root: classes.boxStyled }}>
										<CardContent>
											<Grid container spacing={1} p={1}>
												<Grid item>
													<Typography fontSize="24px" fontWeight="600" color="#404349">
														{t("calibration.label.summary")}
													</Typography>
												</Grid>
												<Grid item xs="auto" display="flex" alignItems="center" ml="auto">
													<Box sx={{ display: "flex", alignItems: "center" }}>
														<Box sx={{ width: "20px", height: "20px", background: cssVariables.color.primary, mr: 1 }} />
														<Typography fontSize="16px" fontWeight="400" color="#AAA">{` = ${t("calibration.label.actual")}`}</Typography>
													</Box>
													<Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
														<Box sx={{ width: "50px", position: "relative" }}>
															<Box sx={{ width: "20px", height: "20px", background: "#F19562", borderRadius: "50%", position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
															<Box sx={{ width: "40px", border: "1px solid #F19562", background: "#F19562", position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
														</Box>
														<Typography fontSize="16px" fontWeight="400" color="#AAA">{` = ${t("calibration.label.guideline")}`}</Typography>
													</Box>
												</Grid>
												<Grid item xs="auto" ml={1}>
													<Select
														id="chartt-type-select"
														inputlabelprops={{ shrink: false }}
														variant="outlined"
														size="small"
														className="white-bg"
														sx={{ width: "200px" }}
														value={selectedChartType}
														onChange={e => setSelectedChartType(e.target.value)}
													>
														<MenuItem value={"number"} key={1}>
															{t("calibration.label.noOfEmployee")}
														</MenuItem>
														<MenuItem value={"percentage"} key={2}>
															{t("calibration.label.percentage")}
														</MenuItem>
													</Select>
												</Grid>
												<Grid item xs={12} mt={0}>
													<Box
														sx={{
															".apexcharts-tooltip-marker": { borderRadius: "2px" },
															".apexcharts-tooltip-y-group > span": {
																fontWeight: "400",
															},
															".apexcharts-tooltip": {
																background: "#F7F7F7",
																fontWeight: "400",
															},
															height: "300px",
															".apexcharts-legend-text": {
																color: "#A3A3A3 !important",
															},
															".apexcharts-text": {
																color: "#A3A3A3 !important",
																fill: "#A3A3A3  !important",
															},
															".apexcharts-tooltip-title": {
																background: "#5DA196 !important",
																color: "#FFFFFF",
																fontWeight: "400",
															},
														}}
													>
														<ApexCharts
															options={chart.options}
															series={chart.series}
															type={chart.options.chart.type}
															height="100%"
														></ApexCharts>
													</Box>
												</Grid>
											</Grid>
										</CardContent>
									</Card>
								</Grid>
							</Grid>
							<Grid container mt={4} mb="80px">
								<Grid item xs={12}>
									<Card classes={{ root: classes.boxStyled }}>
										<CardContent>
											<Grid container sx={{ minHeight: "45px" }}>
												<Grid item display="flex" alignItems="center">
													<Typography fontSize="24px" fontWeight="600" color="#404349">
														{t("calibration.label.calibrationTable")}
													</Typography>
												</Grid>
											</Grid>
											{/* calibration table */}
											<Grid container mt={0}>
												<Grid
													item xs={12}
													sx={{ overflow: "auto", py: 3 }}
													display="flex"
													id="calibration-table"
												>
													{/* calibration card */}
													{
														distributionData?.length > 0 && distributionData?.map((card, index) => {
															return (
																<Paper classes={{ root: classes.calibrationCardRoot }} sx={{ background: card?.serial !== "" && ((parseInt(card?.guideline) - parseInt(card?.employee?.length) > 0) || (parseInt(card?.employee?.length) - parseInt(card?.guideline) > 0)) ? "#F7E7E6" : card?.serial !== "" ? "#EFF6F4" : "#F6F6F6", mr: index < distributionData?.length - 1 ? "30px" : "0" }} key={`dist-${index}`}>
																	<Grid container mb="20px">
																		<Grid item display="flex" alignItems="center">
																			<Box sx={{ maxWidth: "105px", mr: 0.5 }}>
																				<Typography color="#404349" fontWeight="500" fontSize="18px" sx={{ "whiteSpace": "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
																					{card?.seq > 0 && `${card?.seq} - `} {card?.name ?? ""}
																				</Typography>
																			</Box>
																			<BootstrapTooltip
																				title={t("calibration.label.actual")}
																				placement="top"
																			>
																				<Box sx={{ width: "25px", height: "20px", background: "#404349", borderRadius: "2px", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
																					<Typography color="#FFF" fontWeight="500" fontSize="14px" lineHeight={1}>
																						{card?.employee?.length ?? 0}
																					</Typography>
																				</Box>
																			</BootstrapTooltip>
																		</Grid>
																		<Grid item ml="auto" display="flex" alignItems="center">
																			<BootstrapTooltip
																				title={t("calibration.label.guideline")}
																				placement="top"
																			>
																				<DartsIcon />
																			</BootstrapTooltip>
																			<Typography color="#404349" fontWeight="500" fontSize="16px" lineHeight={1} sx={{ ml: 1 }}>
																				{card?.guideline ?? 0}
																			</Typography>
																		</Grid>
																	</Grid>
																	{
																		card?.serial !== "" && (
																			card?.employee?.length < card?.guideline ?
																				<Grid container mb="20px">
																					<Grid item xs={12}>
																						<Paper classes={{ root: classes.calibrationAlert }}>
																							<CalibrationAlertIcon />
																							<Typography color="#FFF" fontWeight="500" fontSize="14px" lineHeight={1} sx={{ ml: 1 }}>
																								{t("calibration.label.pleaseAdd")} {parseInt(card?.guideline) - parseInt(card?.employee?.length)} {parseInt(card?.guideline) - parseInt(card?.employee?.length) > 1 ? t("calibration.label.members") : t("calibration.label.member")}
																							</Typography>
																						</Paper>
																					</Grid>
																				</Grid>
																				:
																				card?.employee?.length > card?.guideline &&
																				<Grid container mb="20px">
																					<Grid item xs={12}>
																						<Paper classes={{ root: classes.calibrationAlert }}>
																							<CalibrationAlertIcon />
																							<Typography color="#FFF" fontWeight="500" fontSize="14px" lineHeight={1} sx={{ ml: 1 }}>
																								{t("calibration.label.pleaseRemove")} {parseInt(card?.employee?.length) - parseInt(card?.guideline)} {parseInt(card?.employee?.length) - parseInt(card?.guideline) > 1 ? t("calibration.label.members") : t("calibration.label.member")}
																							</Typography>
																						</Paper>
																					</Grid>
																				</Grid>
																		)
																	}
																	{/* employee card */}
																	<Grid container item>
																		{
																			card?.employee?.map((employee, index) => {
																				return (
																					<Grid container mb="20px" onClick={() => handleDetailDialog(employee, true)} className="child-calibration-table" key={`emp-${index}`}>
																						<Grid item xs={12} classes={{ root: classes.calibrationItemGrid }}>
																							<Paper classes={{ root: classes.calibrationItemCard }}>
																								<Grid container display="flex" mb="8px">
																									<Grid item xs={11}>
																										<Typography color={cssVariables.color.primary} fontWeight="600" fontSize="16px" lineHeight={1.5} sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
																											{employee?.fName ?? "-"}
																										</Typography>
																										<Typography color={cssVariables.color.primary} fontWeight="600" fontSize="16px" lineHeight={1.5} sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
																											{employee?.lName ?? "-"}
																										</Typography>
																									</Grid>
																									<Grid item ml="auto">
																										<BootstrapTooltip
																											title={t("calibration.label.viewDetails")}
																											placement="top"
																										>
																											<DocumentIcon />
																										</BootstrapTooltip>
																									</Grid>
																								</Grid>
																								<Grid container display="flex" mb="4px" alignItems="center">
																									<Grid item xs={6}>
																										<Typography color="#AAAAAA" fontWeight="400" fontSize="14px" lineHeight={1.5} sx={{ textTransform: "capitalize" }}>
																											{t("calibration.label.score")}
																										</Typography>
																									</Grid>
																									<Grid item xs={6} display="flex" justifyContent="space-between" alignItems="center">
																										<Typography color="#AAAAAA" fontWeight="400" fontSize="14px" lineHeight={1.5}>
																											{t("calibration.label.rating")}:
																										</Typography>
																										<Typography color="#404349" fontWeight="400" fontSize="16px" lineHeight={1.5} sx={{ pr: "13px" }}>
																											{employee?.rating?.seq ?? 0}
																										</Typography>
																									</Grid>
																								</Grid>
																								<Grid container display="flex">
																									<Grid item xs={6} display="flex" alignItems="center">
																										<StarIcon className={classes.starIconSvg} />
																										<Typography color="#404349" fontWeight="400" fontSize="16px" lineHeight={1.5} sx={{ ml: 1 }}>
																											{isNaN(parseFloat(employee?.score?.[Object.keys(employee?.score ?? {})?.[0]]).toFixed(ratingDecimal)) ? "-" : parseFloat(employee?.score?.[Object.keys(employee?.score ?? {})?.[0]]).toFixed(ratingDecimal)}
																										</Typography>
																									</Grid>
																									{
																										(employee?.rating?.seq ?? 0) !== (employee?.calibration?.seq ?? 0) &&
																										<Grid item xs={6} display="flex" justifyContent="space-between" alignItems="center">
																											<Typography color="#AAAAAA" fontWeight="400" fontSize="14px" lineHeight={1.5} sx={{}}>
																												{t("calibration.label.calibration")}:
																											</Typography>
																											<Typography color="#4085C9" fontWeight="400" fontSize="16px" lineHeight={1.5} sx={{ pr: "13px" }}>
																												{employee?.calibration?.seq ?? 0}
																											</Typography>
																										</Grid>
																									}
																								</Grid>
																							</Paper>
																						</Grid>
																					</Grid>
																				)
																			})
																		}
																	</Grid>
																</Paper>
															)
														})
													}
													{/* end calibration card */}
												</Grid>
											</Grid>
											{/* end calibration table */}
										</CardContent>
									</Card>
								</Grid>
							</Grid>
						</Grid>
						<Grid
							item
							xs={toggleSteppers ? 3 : "auto"}
						>
							<StyledPaper
								elevation={3}
								sx={{
									marginLeft: "auto",
									padding: "20px",
									width: toggleSteppers ? "100%" : "fit-content",
								}}
							>
								<Box sx={{ maxWidth: 400 }} id="steppers">
									<Box sx={{ display: "flex" }}>
										{toggleSteppers ? (
											<Typography variant="h3">{t("inbox.label.status")}</Typography>
										) : (
											<></>
										)}
										<Button
											size="small"
											color="black"
											variant="outlined"
											sx={{
												width: "30px",
												height: "30px",
												padding: "0",
												minWidth: "auto",
												marginLeft: "auto",
											}}
											onClick={handleSteppersTransition}
										>
											{toggleSteppers ? (
												<MinimizeIcon></MinimizeIcon>
											) : (
												<MaximizeIcon></MaximizeIcon>
											)}
										</Button>
									</Box>
									<Stepper
										orientation="vertical"
										sx={{
											flexDirection: "column-reverse",
											overflow: "auto",
											maxHeight: "inherit",
											marginTop: "10px",
										}}
										connector={
											toggleSteppers ? (
												<StepConnector />
											) : (
												<StepConnector sx={{ margin: "0 auto" }} />
											)
										}
									>
										{workflow.length > 0 ? (
											workflow.map((step, index) => (
												<Step
													key={step.label}
													expanded={true}
													active={step.active}
													last={index == 0 ? true : false}
													sx={
														!toggleSteppers
															? { display: "flex", margin: "0 auto" }
															: {}
													}
												>
													<StepLabel
														StepIconProps={{
															classes: {
																active:
																	step.stepStatus?.toLowerCase() === "in progress" || step.stepStatus?.toLowerCase() === "ระหว่างดำเนินการ"
																		? classes.processText
																		: step.stepStatus?.toLowerCase() === "completed" || step.stepStatus?.toLowerCase() === "อนุมัติ" || step.stepStatus?.toLowerCase() === "approved" || step.stepStatus?.toLowerCase() === "เสร็จสิ้น" || step.stepStatus?.toLowerCase() === "อนุมัติเสร็จสิ้น"
																			? classes.completedText
																			: step.stepStatus?.toLowerCase() === "rejected" || step.stepStatus?.toLowerCase() === "ปฏิเสธ"
																				? classes.redText
																				: classes.grayText,
																text: !step.active ? classes.stepTextRoot : "",
															},
														}}
														classes={{
															iconContainer: toggleSteppers
																? classes.paddingRight_8
																: classes.paddingRight_0,
														}}
													>
														{toggleSteppers ? (
															<Typography fontWeight={600} sx={{ textTransform: "capitalize" }}>
																{step.position}
															</Typography>
														) : (
															""
														)}
													</StepLabel>
													{toggleSteppers ? (
														step.mark === "approver" ? (
															(step.employee ?? false) &&
																(step.employee.length > 0 ?? false) ? (
																step.employee.map((item, index) => {
																	let isDivide =
																		step.employee.length - 1 === index
																			? false
																			: true;
																	{
																		/* console.log(step.stepStatus)  */
																	}
																	return (
																		<CustomStepContent
																			data={item}
																			readmore={handleOpenReadMoreDialog}
																			divider={isDivide}
																			key={`stepContent-${index}`}
																		/>
																	);
																})
															) : (
																<></>
															)
														) : (
															<CustomStepContent
																data={step}
																readmore={handleOpenReadMoreDialog}
																divider={false}
															/>
														)
													) : (
														<></>
													)}
												</Step>
											))
										) : (
											<></>
										)}
									</Stepper>
								</Box>
							</StyledPaper>
						</Grid>
					</Grid>
					{/* calibration detail dialog */}
					<DetailDialog
						open={isDetailDialogOpen}
						setValue={() => setIsDetailDialogOpen(false)}
						detailData={selectedDetailEmployeeData}
						canEdit={action?.edit}
						ratingDecimal={ratingDecimal}
					/>
					{/* confirm dialog */}
					<ConfirmInboxDialog
						open={openApproveConfirmDialog}
						action={handleApproval}
						setValue={handleCloseApproveConfirmDialog}
						type="approve"
						title={t("inbox.label.dialog.confirmApproval")}
						text={t("inbox.label.dialog.sureConfirmApproval")}
					/>
					<ConfirmInboxDialog
						open={openRejectConfirmDialog}
						action={handleReject}
						setValue={handleCloseRejectConfirmDialog}
						type="reject"
						title={t("inbox.label.dialog.rejectApproval")}
						text={t("inbox.label.dialog.sureRejectApproval")}
					/>
					{/* message dialog */}
					<MessageDialog
						open={openFailedMessageDialog}
						setValue={handleCloseFailedMessageDialog}
						type="fail"
						title={t("inbox.label.dialog.fail")}
						text={failMessage}
					/>
					<MessageDialog
						open={openSucceedMessageDialog}
						setValue={handleCloseSucceedMessageDialog}
						type="success"
						title={t("inbox.label.dialog.success")}
						text={messageSuccess}
					/>
					<ReadMoreDialog
						open={openReadMoreDialog}
						setValue={handleCloseReadMoreDialog}
						title={t("inbox.label.dialog.comment")}
						data={readMoreComment}
					/>
				</Container>
			</Box>
		</>
	)
}
export default InboxCalibrationDetail
